import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { clearReferralCode, readReferralCode } from '../utils/storage';

const AuthContext = createContext();

function extractAuthError(error, fallbackKey) {
  const data = error?.response?.data;
  return {
    error: data?.error,
    errorKey: data?.error_key,
    errorParams: data?.error_params || {},
    fallbackKey,
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/user/profile');
      setUser(response.data || null);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.post('/api/login', {
        email: email.trim().toLowerCase(),
        password,
      });

      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const err = extractAuthError(error, 'signInFailed');
      return {
        success: false,
        ...err,
      };
    }
  }, []);

  const register = useCallback(async (username, email, phone, password, dateOfBirth = null) => {
    try {
      const referralLinkCode = readReferralCode();

      const requestData = {
        username,
        email,
        phone,
        password
      };

      if (dateOfBirth) {
        requestData.dateOfBirth = dateOfBirth;
      }

      if (referralLinkCode) {
        requestData.referralLinkCode = referralLinkCode;
      }

      const response = await api.post('/api/register', requestData);

      if (referralLinkCode) {
        clearReferralCode();
      }

      return { success: true, data: response.data };
    } catch (error) {
      const err = extractAuthError(error, 'registrationFailedGeneric');
      return {
        success: false,
        ...err,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/logout', {});
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setUser(null);
    }
  }, []);

  const registerCompany = useCallback(async (companyName, email, password) => {
    try {
      const response = await api.post('/api/company/register', {
        companyName,
        email,
        password,
      });
      setUser(response.data.user);
      return { success: true, data: response.data };
    } catch (error) {
      const err = extractAuthError(error, 'companyRegistrationFailed');
      return {
        success: false,
        ...err,
      };
    }
  }, []);

  const loginCompany = useCallback(async (email, password) => {
    try {
      const response = await api.post('/api/company/login', {
        email,
        password,
      });
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const isNetworkError = !error.response;
      const err = extractAuthError(
        error,
        isNetworkError ? 'cannotReachServer' : 'companySignInFailed',
      );
      return {
        success: false,
        ...err,
      };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    loginCompany,
    register,
    registerCompany,
    logout,
    checkAuthStatus,
    refreshUser: checkAuthStatus,
    setUser,
  }), [
    user,
    loading,
    login,
    loginCompany,
    register,
    registerCompany,
    logout,
    checkAuthStatus,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export { api };